import { FFmpeg } from "@ffmpeg/ffmpeg";
import { fetchFile, toBlobURL } from "@ffmpeg/util";
import type {
  AudioChannels,
  AudioSampleRate,
  ConversionSettings,
  TargetMimeType,
} from "../types";

const CORE_BASE_URL = "/ffmpeg";

let ffmpeg: FFmpeg | null = null;
let loadPromise: Promise<FFmpeg> | null = null;

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

const normalizeBitrate = (bitrateKbps: number): string =>
  `${Math.round(clamp(bitrateKbps, 64, 320))}k`;

const getFileExtension = (filename: string): string => {
  const ext = filename.split(".").pop();
  return ext ? ext.toLowerCase() : "";
};

const getInputName = (file: File): string => {
  const ext = getFileExtension(file.name) || "wav";
  return `input.${ext}`;
};

const getOutputName = (targetFormat: TargetMimeType): string => {
  if (targetFormat === "audio/wav") return "output.wav";
  if (targetFormat === "audio/mp4") return "output.m4a";
  if (targetFormat === "audio/flac") return "output.flac";
  if (targetFormat === "audio/ogg") return "output.ogg";
  return "output.mp3";
};

const getSampleRateArgs = (sampleRate: AudioSampleRate): string[] =>
  sampleRate === "original" ? [] : ["-ar", sampleRate];

const getChannelArgs = (channels: AudioChannels): string[] =>
  channels === "original" ? [] : ["-ac", channels];

const loadFfmpeg = async (): Promise<FFmpeg> => {
  if (ffmpeg) return ffmpeg;
  if (!loadPromise) {
    const instance = new FFmpeg();
    loadPromise = Promise.all([
      toBlobURL(`${CORE_BASE_URL}/ffmpeg-core.js`, "text/javascript"),
      toBlobURL(`${CORE_BASE_URL}/ffmpeg-core.wasm`, "application/wasm"),
    ])
      .then(([coreURL, wasmURL]) => instance.load({ coreURL, wasmURL }))
      .then(() => {
        ffmpeg = instance;
        return instance;
      });
  }
  return loadPromise;
};

const deleteVirtualFile = async (
  instance: FFmpeg,
  filename: string,
): Promise<void> => {
  try {
    await instance.deleteFile(filename);
  } catch {
    // FFmpeg's virtual FS throws for missing files; cleanup is best-effort.
  }
};

const buildCommand = (
  inputName: string,
  outputName: string,
  targetFormat: TargetMimeType,
  settings: ConversionSettings,
): string[] => {
  const sharedArgs = [
    "-y",
    "-i",
    inputName,
    "-vn",
    ...getSampleRateArgs(settings.sampleRate),
    ...getChannelArgs(settings.channels),
  ];

  if (targetFormat === "audio/wav") {
    return [...sharedArgs, "-c:a", "pcm_s16le", outputName];
  }

  if (targetFormat === "audio/flac") {
    return [
      ...sharedArgs,
      "-c:a",
      "flac",
      "-compression_level",
      "5",
      outputName,
    ];
  }

  if (targetFormat === "audio/mp4") {
    return [
      ...sharedArgs,
      "-c:a",
      "aac",
      "-b:a",
      normalizeBitrate(settings.bitrateKbps),
      outputName,
    ];
  }

  if (targetFormat === "audio/ogg") {
    return [
      ...sharedArgs,
      "-c:a",
      "libopus",
      "-b:a",
      normalizeBitrate(settings.bitrateKbps),
      outputName,
    ];
  }

  return [
    ...sharedArgs,
    "-c:a",
    "libmp3lame",
    "-b:a",
    normalizeBitrate(settings.bitrateKbps),
    outputName,
  ];
};

export const convertAudioWithFfmpeg = async (
  file: File,
  targetFormat: TargetMimeType,
  settings: ConversionSettings,
  onProgress?: (progress: number) => void,
): Promise<Blob> => {
  const inputName = getInputName(file);
  const outputName = getOutputName(targetFormat);
  const ffmpegLogs: string[] = [];
  let lastProgress = -1;
  const emitProgress = (value: number): void => {
    if (!onProgress) return;
    const rounded = Math.round(clamp(value, 0, 100));
    if (rounded !== lastProgress) {
      lastProgress = rounded;
      onProgress(rounded);
    }
  };

  try {
    const instance = await loadFfmpeg();
    instance.on("log", ({ message }) => {
      if (message) ffmpegLogs.push(message);
    });
    if (onProgress) {
      instance.on("progress", ({ progress }) => {
        emitProgress(clamp(progress, 0, 1) * 100);
      });
    }

    await Promise.all([
      deleteVirtualFile(instance, inputName),
      deleteVirtualFile(instance, outputName),
    ]);
    await instance.writeFile(inputName, await fetchFile(file));
    const exitCode = await instance.exec(
      buildCommand(inputName, outputName, targetFormat, settings),
    );
    if (exitCode !== 0) {
      throw new Error(
        ffmpegLogs.at(-1) ?? `FFmpeg exited with code ${exitCode}.`,
      );
    }
    emitProgress(100);

    const data = await instance.readFile(outputName);
    const bytes =
      typeof data === "string" ? new TextEncoder().encode(data) : data;
    if (bytes.byteLength === 0) {
      throw new Error("FFmpeg produced an empty output file.");
    }
    const outputBuffer = bytes.buffer.slice(
      bytes.byteOffset,
      bytes.byteOffset + bytes.byteLength,
    ) as ArrayBuffer;
    return new Blob([outputBuffer], { type: targetFormat });
  } catch (err) {
    const message =
      err instanceof Error
        ? err.message
        : (ffmpegLogs.at(-1) ?? String(err || "Unknown conversion error."));
    throw new Error(`[${file.name}] ${message}`, { cause: err });
  }
};

export const resetFfmpegForTests = (): void => {
  ffmpeg = null;
  loadPromise = null;
};
